import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, Send, Loader2, Download, Mail, Clock, 
  Users, DollarSign, Ticket, CheckCircle, Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import EventSubNav from '../../components/EventSubNav';
import { eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const EventReportsPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [customEmail, setCustomEmail] = useState('');

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventRes, reportRes, historyRes] = await Promise.all([
        eventsApi.getById(eventId),
        eventsApi.getReport(eventId).catch(() => ({ data: null })),
        eventsApi.getReportHistory(eventId).catch(() => ({ data: [] }))
      ]);
      setEvent(eventRes.data);
      setReport(reportRes.data);
      setHistory(historyRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReport = async (email = null) => {
    setSending(true);
    try {
      await eventsApi.sendReport(eventId, email || customEmail || null);
      toast.success('Reporte enviado correctamente');
      setCustomEmail('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar reporte');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#1A1A1A] w-1/3 rounded" />
            <div className="h-96 bg-[#1A1A1A] rounded" />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="event-reports-page">
          <EventSubNav eventTitle={event?.title} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-black uppercase tracking-tight">Reportes del Evento</h2>
                <p className="text-[#A1A1AA] text-sm">Estadísticas detalladas y envío de reportes</p>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowReportModal(true)}
                  className="border-[#27272A]"
                >
                  <Eye size={16} className="mr-2" />
                  Ver Reporte Completo
                </Button>
                <Button 
                  onClick={() => handleSendReport()}
                  disabled={sending}
                  className="bg-white text-black hover:bg-[#E5E5E5]"
                >
                  {sending ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
                  Enviar a Productora
                </Button>
              </div>
            </div>

            {/* Quick Stats */}
            {report && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="card-dashboard text-center">
                  <Ticket size={24} className="mx-auto mb-2 text-blue-500" />
                  <p className="text-3xl font-black">{report.summary?.total_tickets_sold || 0}</p>
                  <p className="text-xs text-[#A1A1AA] uppercase">Tickets Vendidos</p>
                </div>
                <div className="card-dashboard text-center">
                  <Users size={24} className="mx-auto mb-2 text-green-500" />
                  <p className="text-3xl font-black">{report.summary?.checked_in || 0}</p>
                  <p className="text-xs text-[#A1A1AA] uppercase">Asistentes</p>
                </div>
                <div className="card-dashboard text-center">
                  <DollarSign size={24} className="mx-auto mb-2 text-[#D4AF37]" />
                  <p className="text-3xl font-black">${(report.summary?.total_revenue || 0).toLocaleString()}</p>
                  <p className="text-xs text-[#A1A1AA] uppercase">Ingreso Bruto</p>
                </div>
                <div className="card-dashboard text-center">
                  <CheckCircle size={24} className="mx-auto mb-2 text-purple-500" />
                  <p className="text-3xl font-black">{report.summary?.attendance_rate || 0}%</p>
                  <p className="text-xs text-[#A1A1AA] uppercase">Tasa Asistencia</p>
                </div>
              </div>
            )}

            {/* Send to Custom Email */}
            <div className="card-dashboard mb-8">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Mail size={18} />
                Enviar Reporte a Email Personalizado
              </h3>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label className="text-xs text-[#52525B]">Email de destino</Label>
                  <Input
                    type="email"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="email@ejemplo.com"
                    className="bg-transparent border-[#27272A]"
                  />
                </div>
                <Button 
                  onClick={() => handleSendReport(customEmail)}
                  disabled={sending || !customEmail}
                  className="self-end bg-white text-black hover:bg-[#E5E5E5]"
                >
                  {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                </Button>
              </div>
            </div>

            {/* Report History */}
            <div className="card-dashboard">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Clock size={18} />
                Historial de Reportes Enviados
              </h3>
              {history.length > 0 ? (
                <div className="space-y-3">
                  {history.map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#27272A]"
                    >
                      <div>
                        <p className="font-medium">{item.sent_to}</p>
                        <p className="text-sm text-[#A1A1AA]">
                          {item.sent_at && format(new Date(item.sent_at), "d 'de' MMMM, yyyy - HH:mm", { locale: es })}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs uppercase font-bold ${
                          item.type === 'automatic' ? 'bg-blue-500/20 text-blue-500' :
                          item.type === 'admin_manual' ? 'bg-purple-500/20 text-purple-500' :
                          'bg-green-500/20 text-green-500'
                        }`}>
                          {item.type === 'automatic' ? 'Automático' : 
                           item.type === 'admin_manual' ? 'Admin' : 'Manual'}
                        </span>
                        {item.sent_by && (
                          <p className="text-xs text-[#52525B] mt-1">por {item.sent_by}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#52525B] py-8">No hay reportes enviados aún</p>
              )}
            </div>
          </motion.div>

          {/* Full Report Modal */}
          <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
            <DialogContent className="bg-[#121212] border-[#27272A] max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-black uppercase">
                  Reporte Completo: {event?.title}
                </DialogTitle>
              </DialogHeader>
              
              {report && (
                <div className="space-y-6 mt-4">
                  {/* Summary */}
                  <div>
                    <h4 className="text-sm font-bold uppercase text-[#D4AF37] mb-3">Resumen General</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg">
                        <p className="text-xs text-[#A1A1AA]">Tickets Vendidos</p>
                        <p className="text-2xl font-black">{report.summary?.total_tickets_sold}</p>
                      </div>
                      <div className="p-3 bg-[#0A0A0A] border border-[#27272A] rounded-lg">
                        <p className="text-xs text-[#A1A1AA]">Asistentes</p>
                        <p className="text-2xl font-black">{report.summary?.checked_in}</p>
                      </div>
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div>
                    <h4 className="text-sm font-bold uppercase text-[#D4AF37] mb-3">Resumen Financiero</h4>
                    <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA]">Total Recaudado</span>
                        <span className="font-mono font-bold text-lg">${report.summary?.total_revenue?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#A1A1AA]">Costo de Servicio (Plataforma)</span>
                        <span className="font-mono text-red-400">- ${report.summary?.service_fees?.toLocaleString()}</span>
                      </div>
                      {(report.summary?.discount_total || 0) > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-amber-400">Descuentos aplicados (códigos)</span>
                          <span className="font-mono text-amber-400" data-testid="admin-report-discount-total">- ${report.summary?.discount_total?.toLocaleString()}</span>
                        </div>
                      )}
                      <hr className="border-[#27272A]" />
                      <div className="flex justify-between items-center">
                        <span className="text-emerald-400 font-semibold">Ingreso Neto (Productora)</span>
                        <span className="font-mono font-black text-xl text-emerald-400">${report.summary?.net_revenue?.toLocaleString()}</span>
                      </div>
                    </div>
                    <p className="text-xs text-[#71717A] mt-2">
                      El ingreso neto es el monto que corresponde a la productora (sin costos de servicio de la plataforma{(report.summary?.discount_total || 0) > 0 ? ' y con los descuentos por código ya restados' : ''}).
                    </p>
                  </div>

                  {/* Tickets by Tier */}
                  {report.tickets_by_tier && Object.keys(report.tickets_by_tier).length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold uppercase text-[#D4AF37] mb-3">Por Tipo de Entrada</h4>
                      <div className="space-y-2">
                        {Object.entries(report.tickets_by_tier).map(([tier, count]) => (
                          <div key={tier} className="flex justify-between items-center p-3 bg-[#0A0A0A] border border-[#27272A]">
                            <span>{tier}</span>
                            <span className="font-mono font-bold">{count} tickets</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Revenue by Tier */}
                  {report.revenue_by_tier && Object.keys(report.revenue_by_tier).length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold uppercase text-[#D4AF37] mb-3">Ingresos por Tipo</h4>
                      <div className="space-y-2">
                        {Object.entries(report.revenue_by_tier).map(([tier, revenue]) => (
                          <div key={tier} className="flex justify-between items-center p-3 bg-[#0A0A0A] border border-[#27272A]">
                            <span>{tier}</span>
                            <span className="font-mono font-bold text-green-500">${revenue?.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Check-in Methods */}
                  {report.checkin_methods && Object.keys(report.checkin_methods).length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold uppercase text-[#D4AF37] mb-3">Métodos de Check-in</h4>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(report.checkin_methods).map(([method, count]) => (
                          <div key={method} className="p-3 bg-[#0A0A0A] border border-[#27272A] text-center">
                            <p className="text-xl font-black">{count}</p>
                            <p className="text-xs text-[#A1A1AA] uppercase">{method}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default EventReportsPage;
